import React, { useContext, useState } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import DateSelector from '../../common/DateSelector/DateSelector';
import { getInsuranceJourneyMinMaxDob } from '../../common/HelperMethods';
import TextInput from '../../common/TextInput/TextInput';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import { SiteContext } from '../../../context/SiteContext';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../common/CommonTypes';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { PageAbsolutePath } from '../../../consts/Common';

type MobiliseSecurityDialogProps = {
  showDialog: boolean;
  onCloseModal: () => void;
  onContinueModal: () => void;
  cultureCode: string;
  brand: string;
  phoneNumber: string;
};

export type SecurityErrors = {
  dateOfBirth: string;
  postcode: string;
};

const initialErrors = {
  dateOfBirth: '',
  postcode: ''
};

const MobiliseSecurityDialog = ({
  showDialog,
  onCloseModal,
  onContinueModal,
  cultureCode,
  brand,

  phoneNumber
}: MobiliseSecurityDialogProps): JSX.Element => {
  const [updateDobError, setUpdateDobError] = useState(false);
  const [dobIsValid, setDobIsValid] = useState(true);
  const [dateOfBirth, setDateOfBirth] = useState<Date>();
  const [postcode, setPostcode] = useState<string>('');
  const [errors, setErrors] = useState<SecurityErrors>(initialErrors);
  const [showInvalidDetailsMessage, setShowInvalidDetailsMessage] =
    useState(false);
  const navigate = useNavigate();
  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { commonEvents } = pageEvents;
  const { setQuote } = commonEvents;

  const dobRequiredMsg = 'Date of birth is required';
  const dobInvalidMsg = 'Invalid date of birth';
  const ageOutOfRange = 'Age must be between 17 and 90';

  const { site } = useContext(SiteContext);

  const { minDob, maxDob } = getInsuranceJourneyMinMaxDob();

  const today = new Date();

  const isAgeValid = (): boolean => {
    if (dateOfBirth === undefined) {
      return false;
    }

    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())
    ) {
      age--;
    }

    const result = age >= 17 && age <= 90;
    return result;
  };

  const validateDetails = (): boolean => {
    let isValid = true;

    const error: SecurityErrors = {
      ...initialErrors
    };

    if (dobIsValid === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobInvalidMsg;
    } else if (CommonValidation.isEmpty(dateOfBirth)) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobRequiredMsg;
    } else if (isAgeValid() === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = ageOutOfRange;
    }

    if (CommonValidation.isEmpty(postcode)) {
      error.postcode = 'Postcode is required';
      isValid = false;
    } else if (!CommonValidation.isValidPostcode(postcode)) {
      error.postcode = 'Postcode is invalid';
      isValid = false;
    }

    setErrors(error);

    return isValid;
  };

  const getMobiliseCustomerDetails = async (
    dob: Date | undefined,
    postcode: string
  ) => {
    const controller = new AbortController();

    const onSuccess = (insuranceState: InsuranceJourneyState): void => {
      console.log(insuranceState);
      onContinueModal();
      setQuote(insuranceState);
      navigate(PageAbsolutePath.Insurance.AboutYou);
    };

    const onFailure = (error: string): void => {
      setShowInvalidDetailsMessage(true);
      console.log(error);
    };

    await new CarInsuranceApi().GetMobiliseQuoteAsync(
      site.user.token,
      site.brand.tiaProductCode,
      postcode,
      dob?.toISOString() ?? '',
      site.vPol,
      onSuccess,
      onFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onDobValidated = (isValid: boolean): void => {
    setUpdateDobError(false);
    setDobIsValid(isValid);
  };

  const resetForm = () => {
    setDateOfBirth(undefined);
    setPostcode('');
    setShowInvalidDetailsMessage(false);
  };

  const onClickClose = () => {
    resetForm();
    onCloseModal();
  };

  const onClickContinue = async () => {
    const isValid = validateDetails();
    if (isValid) {
      await getMobiliseCustomerDetails(dateOfBirth, postcode);
    }
  };

  const onChangeDateOfBirth = (dob: Date | undefined) => {
    if (dob) {
      setDateOfBirth(dob);
    }
  };

  const onChangePostcode = (postcode: string) => {
    setPostcode(postcode);
  };

  const modalClass = 'modal-dialog-' + brand;

  const invalidDetailsMessage = `Your details do not match our records, please try again or give us
              a call on ${phoneNumber} and we will be happy to help.`;

  return (
    <Modal
      role={modalClass}
      className={`insurance-${brand}`}
      isOpen={showDialog}
      toggle={onClickClose}>
      <ModalHeader toggle={onClickClose}>
        For security, please complete the following fields
      </ModalHeader>
      <ModalBody>
        <div>
          <div>
            <div className='control-label'>Date of birth</div>
            <div className='control'>
              <DateSelector
                value={dateOfBirth}
                minDate={minDob}
                maxDate={maxDob}
                invalidDateMessage={errors.dateOfBirth}
                updateDobError={updateDobError}
                onChange={(value) => onChangeDateOfBirth(value)}
                onValidated={(isValid: boolean) => onDobValidated(isValid)}
                name='date-of-birth'
                culture={cultureCode}
              />
            </div>
          </div>
          <div className='m2-bottom'>
            <div className='control-label'>Postcode</div>
            <div className='control'>
              <TextInput
                name='postcode'
                className='input-text'
                error={errors.postcode}
                value={postcode}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangePostcode(e.currentTarget.value)
                }
              />
            </div>
          </div>
          {showInvalidDetailsMessage && (
            <div className='invalid-details-message error-style'>
              {invalidDetailsMessage}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <ButtonContinue
          name='btn-continue'
          text='Continue'
          onClick={() => {
            onClickContinue();
          }}
          additionalClassNames='btn-ok'
        />
        <ButtonContinue
          name='btn-close'
          text='Close'
          onClick={onClickClose}
          additionalClassNames='btn-cancel'
        />
      </ModalFooter>
    </Modal>
  );
};

export default MobiliseSecurityDialog;
